import {
  DocumentationItem,
  DocumentationListElementsResponse,
  DocumentationSearchResponse,
  DocumentationDependenciesResponse,
  DocumentationDependentsResponse,
} from './documentation';

export enum Data360ElementType {
  // REST API accessible types
  DATA_MODEL_OBJECT = 'DATA_MODEL_OBJECT',
  DATA_MODEL_FIELD = 'DATA_MODEL_FIELD',
  DATA_LAKE_OBJECT = 'DATA_LAKE_OBJECT',
  DATA_LAKE_FIELD = 'DATA_LAKE_FIELD',
  CALCULATED_INSIGHT = 'CALCULATED_INSIGHT',
  INSIGHT_DIMENSION = 'INSIGHT_DIMENSION',
  INSIGHT_MEASURE = 'INSIGHT_MEASURE',
  DATA_GRAPH = 'DATA_GRAPH',
  SEGMENT = 'SEGMENT',
  IDENTITY_RESOLUTION_RULESET = 'IDENTITY_RESOLUTION_RULESET',
  IDENTITY_RESOLUTION_RULE = 'IDENTITY_RESOLUTION_RULE',
  DATA_ACTION = 'DATA_ACTION',

  // Connect API types
  DATA_SPACE = 'DATA_SPACE',
  ACTIVATION = 'ACTIVATION',
  ACTIVATION_TARGET = 'ACTIVATION_TARGET',
  ACTIVATION_EXTERNAL_PLATFORM = 'ACTIVATION_EXTERNAL_PLATFORM',
  CLEAN_ROOM_COLLABORATION = 'CLEAN_ROOM_COLLABORATION',
  CLEAN_ROOM_PROVIDER = 'CLEAN_ROOM_PROVIDER',
  CLEAN_ROOM_TEMPLATE = 'CLEAN_ROOM_TEMPLATE',
  SEARCH_INDEX = 'SEARCH_INDEX',
  DATA_SHARE = 'DATA_SHARE',
  DATA_SHARE_TARGET = 'DATA_SHARE_TARGET',
  DATA_ACTION_TARGET = 'DATA_ACTION_TARGET',
  SEMANTIC_LAYER = 'SEMANTIC_LAYER',

  // Metadata API (XML) only types
  DATA_STREAM_DEFINITION = 'DATA_STREAM_DEFINITION',
  ACTIVATION_PLATFORM = 'ACTIVATION_PLATFORM',
  MARKET_SEGMENT_ACTIVATION = 'MARKET_SEGMENT_ACTIVATION',
  DATA_SOURCE = 'DATA_SOURCE',
  DATA_SOURCE_OBJECT = 'DATA_SOURCE_OBJECT',
  DATA_SOURCE_TENANT = 'DATA_SOURCE_TENANT',
  EXTERNAL_DATA_CONNECTOR = 'EXTERNAL_DATA_CONNECTOR',
  EXTERNAL_DATA_SOURCE = 'EXTERNAL_DATA_SOURCE',
  DATA_CONNECTOR = 'DATA_CONNECTOR',
  DATA_STREAM_TEMPLATE = 'DATA_STREAM_TEMPLATE',
  DATA_SOURCE_BUNDLE_DEFINITION = 'DATA_SOURCE_BUNDLE_DEFINITION',
  DATA_SRC_DATA_MODEL_FIELD_MAP = 'DATA_SRC_DATA_MODEL_FIELD_MAP',
  DATA_PACKAGE_KIT_DEFINITION = 'DATA_PACKAGE_KIT_DEFINITION',
  DATA_PACKAGE_KIT_OBJECT = 'DATA_PACKAGE_KIT_OBJECT',
  DATA_TRANSFORM = 'DATA_TRANSFORM',

  // Mapping & Relationship types
  FIELD_MAPPING = 'FIELD_MAPPING',
  OBJECT_MAPPING = 'OBJECT_MAPPING',
}

export interface Data360ItemBase<T extends Data360ElementType = Data360ElementType>
  extends DocumentationItem {
  parentType: T;
  description?: string;
  body?: string;
}

// #1 Data Model Object (DMO)
export interface Data360DataModelObjectItem
  extends Data360ItemBase<Data360ElementType.DATA_MODEL_OBJECT> {
  category?: string;
  referenceModelEntity?: string;
  rawData?: string;
}

// #2 Data Model Field (DMO Field)
export interface Data360DataModelFieldItem
  extends Data360ItemBase<Data360ElementType.DATA_MODEL_FIELD> {
  businessType?: string;
  keyQualifier?: string;
  parentObject?: string;
}

// #3 Data Lake Object (DLO)
export interface Data360DataLakeObjectItem
  extends Data360ItemBase<Data360ElementType.DATA_LAKE_OBJECT> {
  category?: string;
  referenceModelEntity?: string;
}

// #4 Data Lake Field (DLO Field)
export interface Data360DataLakeFieldItem
  extends Data360ItemBase<Data360ElementType.DATA_LAKE_FIELD> {
  businessType?: string;
  keyQualifier?: string;
  parentObject?: string;
}

// #5 Data Graph
export interface Data360DataGraphItem
  extends Data360ItemBase<Data360ElementType.DATA_GRAPH> {
  developerName?: string;
  dataspace?: string;
  primaryObject?: string;
  version?: string;
  primaryObjectType?: string;
  status?: string;
  valuesDmo?: string;
  idDmo?: string;
}

// #6 Data Source
export interface Data360DataSourceItem
  extends Data360ItemBase<Data360ElementType.DATA_SOURCE> {
  displayName?: string;
  apiName?: string;
}

// #7 Data Source Object
export interface Data360DataSourceObjectItem
  extends Data360ItemBase<Data360ElementType.DATA_SOURCE_OBJECT> {
  displayName?: string;
  apiName?: string;
  objectName?: string;
  dataSource?: string;
}

// #8 Data Source Tenant
export interface Data360DataSourceTenantItem
  extends Data360ItemBase<Data360ElementType.DATA_SOURCE_TENANT> {
  displayName?: string;
  apiName?: string;
  tenantId?: string;
  dataSource?: string;
}

// #9 Data Connector
export interface Data360DataConnectorItem
  extends Data360ItemBase<Data360ElementType.DATA_CONNECTOR> {
  displayName?: string;
  apiName?: string;
  connectorType?: string;
  bucketName?: string;
}

// #10 External Data Connector
export interface Data360ExternalDataConnectorItem
  extends Data360ItemBase<Data360ElementType.EXTERNAL_DATA_CONNECTOR> {
  displayName?: string;
  apiName?: string;
  connectorType?: string;
}

// #11 External Data Source
export interface Data360ExternalDataSourceItem
  extends Data360ItemBase<Data360ElementType.EXTERNAL_DATA_SOURCE> {
  sourceType?: string;
}

// #15 Data Stream Definition
export interface Data360DataStreamDefinitionItem
  extends Data360ItemBase<Data360ElementType.DATA_STREAM_DEFINITION> {
  displayName?: string;
  apiName?: string;
  category?: string;
  dataSource?: string;
  externalDataConnector?: string;
}

// #16 Data Stream Template
export interface Data360DataStreamTemplateItem
  extends Data360ItemBase<Data360ElementType.DATA_STREAM_TEMPLATE> {}

// #17 Data Transform
export interface Data360DataTransformItem
  extends Data360ItemBase<Data360ElementType.DATA_TRANSFORM> {
  displayName?: string;
  apiName?: string;
  transformType?: string;
}

// #18 Data Source Bundle Definition
export interface Data360DataSourceBundleDefinitionItem
  extends Data360ItemBase<Data360ElementType.DATA_SOURCE_BUNDLE_DEFINITION> {
  dataSource?: string;
}

// #19 Data Src Data Model Field Map
export interface Data360DataSrcDataModelFieldMapItem
  extends Data360ItemBase<Data360ElementType.DATA_SRC_DATA_MODEL_FIELD_MAP> {
  sourceField?: string;
  targetField?: string;
  dataSourceObject?: string;
}

// #20 Field Mapping
export interface Data360FieldMappingItem
  extends Data360ItemBase<Data360ElementType.FIELD_MAPPING> {
  masterLabel?: string;
  sourceField?: string;
  targetField?: string;
  dataSourceObject?: string;
  cardinality?: string;
  targetEntity?: string;
}

// #21 Object Mapping
export interface Data360ObjectMappingItem
  extends Data360ItemBase<Data360ElementType.OBJECT_MAPPING> {
  sourceObject?: string;
  targetObject?: string;
}

// #23 Calculated Insight
export interface Data360CalculatedInsightItem
  extends Data360ItemBase<Data360ElementType.CALCULATED_INSIGHT> {
  partitionBy?: string;
  latestProcessTime?: string;
  latestSuccessfulProcessTime?: string;
}

// #24 Insight Dimension
export interface Data360InsightDimensionItem
  extends Data360ItemBase<Data360ElementType.INSIGHT_DIMENSION> {
  parentInsight?: string;
}

// #25 Insight Measure
export interface Data360InsightMeasureItem
  extends Data360ItemBase<Data360ElementType.INSIGHT_MEASURE> {
  rollupable?: string;
  parentInsight?: string;
}

// #26 Segment
export interface Data360SegmentItem
  extends Data360ItemBase<Data360ElementType.SEGMENT> {
  displayName?: string;
  segmentType?: string;
  segmentStatus?: string;
  dataspace?: string;
  segmentOn?: string;
  marketSegmentDefinition?: string;
  publishStatus?: string;
}

// #27 Market Segment Activation
export interface Data360MarketSegmentActivationItem
  extends Data360ItemBase<Data360ElementType.MARKET_SEGMENT_ACTIVATION> {
  displayName?: string;
  apiName?: string;
  segmentationType?: string;
}

// #29 Activation
export interface Data360ActivationItem
  extends Data360ItemBase<Data360ElementType.ACTIVATION> {
  developerName?: string;
  activationType?: string;
  status?: string;
  sourceSegment?: string;
  lastModified?: string;
}

// #30 Activation Target
export interface Data360ActivationTargetItem
  extends Data360ItemBase<Data360ElementType.ACTIVATION_TARGET> {
  developerName?: string;
  targetType?: string;
  platform?: string;
  status?: string;
  lastModified?: string;
}

// #31 Activation External Platform
export interface Data360ActivationExternalPlatformItem
  extends Data360ItemBase<Data360ElementType.ACTIVATION_EXTERNAL_PLATFORM> {
  developerName?: string;
  platformType?: string;
  status?: string;
  lastModified?: string;
}

// #32 Activation Platform
export interface Data360ActivationPlatformItem
  extends Data360ItemBase<Data360ElementType.ACTIVATION_PLATFORM> {
  isActive?: string;
}

// #33 Data Action
export interface Data360DataActionItem
  extends Data360ItemBase<Data360ElementType.DATA_ACTION> {
  displayName?: string;
  masterLabel?: string;
  status?: string;
  dataSpace?: string;
  lastStatusDate?: string;
  triggerOnlyFirstTime?: string;
  sourceCount?: string;
  targetCount?: string;
}

// #34 Data Action Target
export interface Data360DataActionTargetItem
  extends Data360ItemBase<Data360ElementType.DATA_ACTION_TARGET> {
  apiName?: string;
  status?: string;
  lastModified?: string;
}

// #35 Data Space
export interface Data360DataSpaceItem
  extends Data360ItemBase<Data360ElementType.DATA_SPACE> {
  displayName?: string;
  isDefault?: string;
  status?: string;
  createdDate?: string;
  lastModified?: string;
}

// #36 Identity Resolution Ruleset
export interface Data360IdentityResolutionRulesetItem
  extends Data360ItemBase<Data360ElementType.IDENTITY_RESOLUTION_RULESET> {
  status?: string;
  lastModified?: string;
}

// #37 Identity Resolution Rule
export interface Data360IdentityResolutionRuleItem
  extends Data360ItemBase<Data360ElementType.IDENTITY_RESOLUTION_RULE> {
  order?: string;
  ruleset?: string;
}

// #38 Clean Room Collaboration
export interface Data360CleanRoomCollaborationItem
  extends Data360ItemBase<Data360ElementType.CLEAN_ROOM_COLLABORATION> {
  status?: string;
  provider?: string;
  createdDate?: string;
  lastModified?: string;
}

// #39 Clean Room Provider
export interface Data360CleanRoomProviderItem
  extends Data360ItemBase<Data360ElementType.CLEAN_ROOM_PROVIDER> {}

// #40 Clean Room Template
export interface Data360CleanRoomTemplateItem
  extends Data360ItemBase<Data360ElementType.CLEAN_ROOM_TEMPLATE> {
  provider?: string;
}

// #41 Data Share
export interface Data360DataShareItem
  extends Data360ItemBase<Data360ElementType.DATA_SHARE> {
  status?: string;
  dataSpace?: string;
  createdDate?: string;
  lastModified?: string;
}

// #42 Data Share Target
export interface Data360DataShareTargetItem
  extends Data360ItemBase<Data360ElementType.DATA_SHARE_TARGET> {
  targetType?: string;
  status?: string;
  dataShare?: string;
  lastModified?: string;
}

// #43 Data Package Kit Definition
export interface Data360DataPackageKitDefinitionItem
  extends Data360ItemBase<Data360ElementType.DATA_PACKAGE_KIT_DEFINITION> {
  isTemplate?: string;
}

// #44 Data Package Kit Object
export interface Data360DataPackageKitObjectItem
  extends Data360ItemBase<Data360ElementType.DATA_PACKAGE_KIT_OBJECT> {
  dataPackageKit?: string;
  objectApiName?: string;
}

// #45 Search Index
export interface Data360SearchIndexItem
  extends Data360ItemBase<Data360ElementType.SEARCH_INDEX> {
  developerName?: string;
  objectName?: string;
  status?: string;
  lastModified?: string;
}

// #46 Semantic Layer
export interface Data360SemanticLayerItem
  extends Data360ItemBase<Data360ElementType.SEMANTIC_LAYER> {}

export type Data360ConfigurationItem =
  | Data360DataModelObjectItem
  | Data360DataModelFieldItem
  | Data360DataLakeObjectItem
  | Data360DataLakeFieldItem
  | Data360DataGraphItem
  | Data360DataSourceItem
  | Data360DataSourceObjectItem
  | Data360DataSourceTenantItem
  | Data360DataConnectorItem
  | Data360ExternalDataConnectorItem
  | Data360ExternalDataSourceItem
  | Data360DataStreamDefinitionItem
  | Data360DataStreamTemplateItem
  | Data360DataTransformItem
  | Data360DataSourceBundleDefinitionItem
  | Data360DataSrcDataModelFieldMapItem
  | Data360FieldMappingItem
  | Data360ObjectMappingItem
  | Data360CalculatedInsightItem
  | Data360InsightDimensionItem
  | Data360InsightMeasureItem
  | Data360SegmentItem
  | Data360MarketSegmentActivationItem
  | Data360ActivationItem
  | Data360ActivationTargetItem
  | Data360ActivationExternalPlatformItem
  | Data360ActivationPlatformItem
  | Data360DataActionItem
  | Data360DataActionTargetItem
  | Data360DataSpaceItem
  | Data360IdentityResolutionRulesetItem
  | Data360IdentityResolutionRuleItem
  | Data360CleanRoomCollaborationItem
  | Data360CleanRoomProviderItem
  | Data360CleanRoomTemplateItem
  | Data360DataShareItem
  | Data360DataShareTargetItem
  | Data360DataPackageKitDefinitionItem
  | Data360DataPackageKitObjectItem
  | Data360SearchIndexItem
  | Data360SemanticLayerItem;

export type Data360TabType = Data360ElementType;

export type Data360DocumentationListElementsResponse =
  DocumentationListElementsResponse<Data360ConfigurationItem>;
export type Data360DocumentationSearchResponse =
  DocumentationSearchResponse<Data360ConfigurationItem>;
export type Data360DocumentationDependenciesResponse =
  DocumentationDependenciesResponse<Data360ConfigurationItem>;
export type Data360DocumentationDependentsResponse =
  DocumentationDependentsResponse<Data360ConfigurationItem>;

export interface Data360DocumentationChildrenResponse {
  children: Record<string, Data360ConfigurationItem[]>;
}
